if (!window.ReadMore) {
  class ReadMore {
    constructor(container) {
      if (!container?.nodeType) return;

      this.container = container;
      this.content = container.querySelector('.read-more-content');
      this.button = container.querySelector('[data-read-more-button]');
      this.readMoreText = this.button?.querySelector('.read-more-text');
      this.readLessText = this.button?.querySelector('.read-less-text');
      this.maxHeight = Math.max(parseInt(this.content?.dataset.maxHeight) || 100, 0);
      this.isExpanded = false;
      this.isDestroyed = false;
      this.timeoutId = null;
      this.rafId = null;
      this.resizeTimeoutId = null;

      // Bind methods once
      this.handleClick = this.toggle.bind(this);
      this.handleResize = this.debouncedCheckOverflow.bind(this);

      if (this.content && this.button) this.init();
    }

    init() {
      if (this.isDestroyed) return;

      // Set initial collapsed state
      this.content.classList.remove('is-expanded');
      this.content.style.maxHeight = `${this.maxHeight}px`;

      this.checkOverflow();
      this.button.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize);
    }

    debouncedCheckOverflow() {
      if (this.resizeTimeoutId) clearTimeout(this.resizeTimeoutId);
      this.resizeTimeoutId = setTimeout(() => {
        this.checkOverflow();
        this.resizeTimeoutId = null;
      }, 150);
    }

    checkOverflow() {
      if (this.isDestroyed || !this.content) return;

      // Temporarily remove maxHeight to measure actual content
      this.content.style.maxHeight = 'none';
      this.content.offsetHeight; // Force reflow

      const scrollHeight = this.content.scrollHeight;
      const childMargins = this.calculateChildMargins();
      const actualHeight = scrollHeight - childMargins;

      // Update UI based on overflow state
      const hasOverflow = actualHeight > this.maxHeight;
      this.container.dataset.overflow = hasOverflow ? 'true' : 'false';
      this.content.classList.toggle('is-expanded', !hasOverflow);
      this.content.style.maxHeight = hasOverflow ? `${this.maxHeight}px` : 'none';
    }

    calculateChildMargins() {
      const firstChild = this.content.firstElementChild;
      const lastChild = this.content.lastElementChild;

      if (!firstChild) return 0;

      let margins = 0;
      const firstStyle = window.getComputedStyle(firstChild);
      margins += parseFloat(firstStyle.marginTop) || 0;

      // Avoid counting margin twice if only one child
      if (lastChild && lastChild !== firstChild) {
        const lastStyle = window.getComputedStyle(lastChild);
        margins += parseFloat(lastStyle.marginBottom) || 0;
      } else if (firstChild) {
        margins += parseFloat(firstStyle.marginBottom) || 0;
      }

      return margins;
    }

    toggle() {
      if (this.isDestroyed) return;
      this.clearTimers();
      this.isExpanded = !this.isExpanded;
      this.isExpanded ? this.expand() : this.collapse();
    }

    expand() {
      if (!this.content) return;

      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.content.classList.add('is-expanded');
      this.updateButton(true);

      this.timeoutId = setTimeout(() => {
        if (!this.isDestroyed && this.isExpanded && this.content) {
          this.content.style.maxHeight = 'none';
        }
        this.timeoutId = null;
      }, 400);
    }

    collapse() {
      if (!this.content) return;

      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.content.offsetHeight; // Force reflow

      this.rafId = requestAnimationFrame(() => {
        if (this.isDestroyed || !this.content || !this.container) return;

        this.content.style.maxHeight = `${this.maxHeight}px`;
        this.content.classList.remove('is-expanded');
        this.updateButton(false);

        // Scroll to top if element is above viewport
        const rect = this.container.getBoundingClientRect();
        if (rect.top < 0) {
          this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        this.rafId = null;
      });
    }

    updateButton(expanded) {
      if (!this.button || !this.readMoreText || !this.readLessText) return;

      this.button.setAttribute('aria-expanded', expanded);
      this.readMoreText.style.display = expanded ? 'none' : 'inline';
      this.readLessText.style.display = expanded ? 'inline' : 'none';
    }

    clearTimers() {
      if (this.timeoutId) clearTimeout(this.timeoutId);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.resizeTimeoutId) clearTimeout(this.resizeTimeoutId);
      this.timeoutId = this.rafId = this.resizeTimeoutId = null;
    }

    destroy() {
      if (this.isDestroyed) return;
      this.isDestroyed = true;

      // Clear all timers
      this.clearTimers();

      // Remove event listeners
      this.button?.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);

      // Clear references to prevent memory leaks
      this.container = this.content = this.button = null;
      this.readMoreText = this.readLessText = null;
      this.handleClick = this.handleResize = null;
    }

    static initAll() {
      const elements = document.querySelectorAll('[data-read-more-id]');
      const instances = [];

      elements.forEach(el => {
        // Skip if already initialized
        if (el.readMoreInstance) return;

        try {
          const instance = new ReadMore(el);
          if (instance.content && instance.button) {
            el.readMoreInstance = instance;
            instances.push(instance);
          }
        } catch (error) {
          console.error('ReadMore init failed:', error);
        }
      });

      return instances;
    }
  }

  window.ReadMore = ReadMore;

  // Initialize function
  const initReadMore = () => ReadMore.initAll();

  // Cleanup function to prevent memory leaks
  const cleanupReadMore = (target = document) => {
    // Ensure target is a valid element with querySelectorAll method
    if (!target || typeof target.querySelectorAll !== 'function') {
      target = document;
    }

    const elements = target.querySelectorAll('[data-read-more-id]');
    elements.forEach(el => {
      if (el.readMoreInstance) {
        el.readMoreInstance.destroy();
        delete el.readMoreInstance;
      }
    });
  };

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReadMore);
  } else {
    initReadMore();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', () => setTimeout(initReadMore, 100));
  document.addEventListener('shopify:section:unload', (e) => cleanupReadMore(e.target));

  // Cleanup on page unload
  window.addEventListener('beforeunload', cleanupReadMore);
}
